// this file mirrors CLP's course-landing-page-with-providers.react-component.tsx: owns the feature-level provider tree, renders required providers around <LOHPPage />

'use client';

import {
    SubscriptionPlansProvider,
    SubscriptionPlanProductType,
    type SubscriptionPlansQueryVariables,
} from '@udemy-v2/subscription-graphql';
import {CoursePriceProvider} from '@udemy-v2/react-course-pricing';
import {FbtBundleConfigContext, type FbtBundleConfig} from '@udemy-v2/react-bundle-unit';

/**
 * Productionalized FBT-bundle-after-ATC redesign, mirroring v1's `FbtBundleConfigProvider`
 * values (the experiment behind it shipped as the default; nothing left to gate on). v1 also
 * sets `hideRelatedLabelsList: true`, which has no v2 equivalent in `FbtBundleConfig` yet.
 */
const FBT_BUNDLE_CONFIG: FbtBundleConfig = {
    showPerCardAddToCart: true,
    excludeAddedCourseFromBundle: true,
    wideSuccessModal: true,
};

/**
 * Current page URL passed to the subscription plans query as `marketingURL`, mirroring
 * v1 frontends-homepage, which builds `origin + router.asPath` for conversion
 * attribution. `router.asPath` includes the query string and hash, so we include
 * `search` and `hash` here too. Resolves to `undefined` during SSR (no `window`), in
 * which case the variable is simply omitted.
 */
function getMarketingUrl(): string | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const {origin, pathname, search, hash} = window.location;
    return `${origin}${pathname}${search}${hash}`;
}

export function LOHPPageProviders({children}: {children: React.ReactNode}) {
    const marketingURL = getMarketingUrl();

    const subscriptionPlanQueryVariables: SubscriptionPlansQueryVariables[] = [
        {filters: {productType: SubscriptionPlanProductType.Consumersubscription}, marketingURL},
        {
            filters: {productType: SubscriptionPlanProductType.ConsumersubscriptionValuePlan},
            marketingURL,
        },
        {filters: {productType: SubscriptionPlanProductType.GrowWithGoogle}},
        {filters: {productType: SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle}},
    ];

    return (
        <FbtBundleConfigContext.Provider value={FBT_BUNDLE_CONFIG}>
            <SubscriptionPlansProvider subscriptionPlanQueryVariables={subscriptionPlanQueryVariables}>
                <CoursePriceProvider>{children}</CoursePriceProvider>
            </SubscriptionPlansProvider>
        </FbtBundleConfigContext.Provider>
    );
}
